/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useParcels } from "../../store/useParcels";
import { useDropLocations } from "../../store/useDropLocations";
import { useCouriers } from "../../store/useCouriers";
import {
  Tooltip,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";
import {
  MaterialsChartFilters,
  getDefaultMaterialsChartFilters,
  type MaterialsChartFiltersState,
} from "../../pages/Volumes/MaterialsChartFilters";
import {
  getStoreColor,
  getCityColor,
  TOTAL_CONTRAST_COLOR,
} from "../../constants/plasticColors";
import { ApexOptions } from "apexcharts";

dayjs.extend(isoWeek);

interface ParcelRevenuePerStorePerCourierProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const ParcelRevenuePerStorePerCourier = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: ParcelRevenuePerStorePerCourierProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
    /** Refs */
    const snapshotRef = useRef(null);
    /** Hooks */
    const { parcelRevenuePerLocation, getParcelRevenuePerLocation } =
      useParcels();
    const { dropLocations, getDropLocations } = useDropLocations();
    const { allCouriers, getAllCouriers } = useCouriers();

    const [_, takeScreenshot] = useScreenshot();
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);
    /** State */
    const [filters, setFilters] = useState<MaterialsChartFiltersState>(() =>
      getDefaultMaterialsChartFilters()
    );
    const [selectedCourierId, setSelectedCourierId] = useState<string | "ALL">(
      "ALL"
    );
    const [showTotal, setShowTotal] = useState(true);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "parcel_revenue_per_store_per_courier.png";
        link.click();
      });
    };

    const selectedCourierLabel = useMemo(() => {
      if (selectedCourierId === "ALL") return "All couriers";
      const courier = allCouriers?.find((c) => c._id === selectedCourierId);
      return courier?.name ?? "All couriers";
    }, [selectedCourierId, allCouriers]);

    /** Use API response directly - backend handles filtering when !fetchAllMode; frontend filters when fetchAllMode */
    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";
    const TOTAL_SERIES_NAME = "Total";
    const filteredParcelRevenueData = useMemo(() => {
      if (!useGeneralFilter) return parcelRevenuePerLocation ?? [];
      const raw = parcelRevenuePerLocation ?? [];
      if (
        (filterType ?? "location") === "location" &&
        (filterPlaces ?? []).length === 0
      )
        return [];
      const realFilterPlaces =
        filterPlaces?.filter((p) => p !== TOTAL_ALL_STORES_ID) ?? [];
      if (!realFilterPlaces.length) return raw;
      const selectedNames = new Set<string>();
      if ((filterType ?? "location") === "city") {
        realFilterPlaces.forEach((p) => selectedNames.add(p));
      } else {
        realFilterPlaces.forEach((id) => {
          const loc = dropLocations?.find((l) => l._id === id);
          if (loc?.cost_place) selectedNames.add(loc.cost_place);
        });
      }
      return raw.filter((s) => selectedNames.has(s.name));
    }, [
      parcelRevenuePerLocation,
      useGeneralFilter,
      filterType,
      filterPlaces,
      dropLocations,
    ]);

    const includeTotalAllStores = showTotal;

    const chartDataWithTotal = useMemo(() => {
      const base = filteredParcelRevenueData ?? [];
      if (!includeTotalAllStores || base.length === 0) return base;
      const firstSeries = base[0];
      const data = firstSeries?.data as unknown as
        | [number, number][]
        | undefined;
      if (!data || !Array.isArray(data)) return base;
      const totalData = data.map(([x]: [number, number], idx: number) => {
        const sum = base.reduce((acc, s) => {
          const rawData = s?.data as unknown;
          const point = Array.isArray(rawData) ? rawData[idx] : undefined;
          const val = Array.isArray(point)
            ? point[1]
            : typeof point === "number"
              ? point
              : 0;
          return acc + (Number(val) || 0);
        }, 0);
        return [x, sum];
      });
      return [...base, { name: TOTAL_SERIES_NAME, data: totalData }];
    }, [filteredParcelRevenueData, includeTotalAllStores]);

    // For quarter view, aggregate underlying monthly buckets into 4 points
    // per year (Q1..Q4) per series. Month/year views use raw API data.
    const chartSeries = useMemo(() => {
      const base = chartDataWithTotal ?? [];
      if (!base.length) return base;
      if (filters.period !== "quarter") return base;

      const { fromYear, toYear, fromQuarter, toQuarter } = filters;

      return base.map((series) => {
        const raw =
          (series.data as unknown as [number, number][] | undefined) ?? [];
        const sums = new Map<string, number>(); // "year-quarter" -> value
        const bucketTs = new Map<string, number>(); // representative ts per quarter

        raw.forEach(([ts, val]) => {
          const d = dayjs(ts);
          const y = d.year();
          const q = Math.ceil((d.month() + 1) / 3);
          if (y < fromYear || y > toYear) return;
          if (y === fromYear && q < fromQuarter) return;
          if (y === toYear && q > toQuarter) return;

          const key = `${y}-${q}`;
          sums.set(key, (sums.get(key) ?? 0) + (val ?? 0));

          if (!bucketTs.has(key)) {
            // Use middle month of the quarter as representative timestamp
            const middleMonthIndex = (q - 1) * 3 + 1; // 0-based
            const reprTs = new Date(y, middleMonthIndex, 15).getTime();
            bucketTs.set(key, reprTs);
          }
        });

        const data: [number, number][] = [];
        for (let y = fromYear; y <= toYear; y++) {
          const startQ = y === fromYear ? fromQuarter : 1;
          const endQ = y === toYear ? toQuarter : 4;
          for (let q = startQ; q <= endQ; q++) {
            const key = `${y}-${q}`;
            const ts = bucketTs.get(key);
            if (ts == null) continue;
            data.push([ts, sums.get(key) ?? 0]);
          }
        }

        return { ...series, data };
      });
    }, [chartDataWithTotal, filters]);

    const monthlyTickAmount = useMemo(() => {
      return parcelRevenuePerLocation?.length
        ? parcelRevenuePerLocation?.[0]?.data?.length - 1
        : 11;
    }, [parcelRevenuePerLocation]);

    const parcelRevenueSeriesNames = useMemo(
      () => (chartSeries ?? []).map((series) => series?.name || ""),
      [chartSeries]
    );
    const parcelRevenueColors = useMemo(() => {
      const isCityView =
        useGeneralFilter && (filterType ?? "location") === "city";
      return parcelRevenueSeriesNames.map((name) =>
        name === TOTAL_SERIES_NAME
          ? TOTAL_CONTRAST_COLOR
          : isCityView
            ? getCityColor(name)
            : getStoreColor(name)
      );
    }, [parcelRevenueSeriesNames, useGeneralFilter, filterType]);

    /** Effects */
    useEffect(() => {
      getDropLocations();
      getAllCouriers();
    }, []);

    const prevFilterSigRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({
        filters,
        filterType,
        selectedCourierId,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      const typeParam = useGeneralFilter
        ? (filterType ?? "location")
        : undefined;
      const placesParam = undefined; // Always fetch all; frontend filters by filterPlaces
      const {
        period,
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        fromQuarter,
        toQuarter,
        fromWeek,
        toWeek,
      } = filters;
      let startMonth: number;
      let endMonth: number;
      if (period === "month") {
        startMonth = fromMonth;
        endMonth = toMonth;
      } else if (period === "quarter") {
        startMonth = (fromQuarter - 1) * 3 + 1;
        endMonth = toQuarter * 3;
      } else if (period === "year") {
        startMonth = 1;
        endMonth = 12;
      } else {
        // week: map to month range containing those weeks (API only supports months)
        const startOfFromWeek = dayjs()
          .year(fromYear)
          .isoWeek(fromWeek)
          .startOf("isoWeek");
        const endOfToWeek = dayjs()
          .year(toYear)
          .isoWeek(toWeek)
          .endOf("isoWeek");
        startMonth = startOfFromWeek.month() + 1;
        endMonth = endOfToWeek.month() + 1;
      }
      const promise = getParcelRevenuePerLocation(
        fromYear,
        startMonth,
        toYear,
        endMonth,
        typeParam,
        placesParam,
        selectedCourierId === "ALL" ? undefined : selectedCourierId
      );
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
        );
      }
    }, [
      filters,
      getParcelRevenuePerLocation,
      useGeneralFilter,
      filterType,
      selectedCourierId,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
    ]);

    const xTickAmount = useMemo(() => {
      if (
        (filters.period === "quarter" || filters.period === "year") &&
        chartSeries &&
        chartSeries.length > 0
      ) {
        const first = chartSeries[0];
        const data =
          (first.data as unknown as [number, number][] | undefined) ?? [];
        return data.length > 1 ? data.length - 1 : 1;
      }
      return monthlyTickAmount;
    }, [filters.period, chartSeries, monthlyTickAmount]);

    const downloadCsv = () => {
      setIsCsvDownloading(true);
      const params: Record<string, string> = {
        startYear: filters.fromYear.toString(),
        endYear: filters.toYear.toString(),
        period: "daily",
      };
      params.startMonth =
        filters.period === "year"
          ? "1"
          : filters.period === "quarter"
            ? String((filters.fromQuarter - 1) * 3 + 1)
            : String(filters.fromMonth);
      params.endMonth =
        filters.period === "year"
          ? "12"
          : filters.period === "quarter"
            ? String(filters.toQuarter * 3)
            : String(filters.toMonth);
      if (useGeneralFilter) params.type = filterType ?? "location";
      if (selectedCourierId !== "ALL") params.courierId = selectedCourierId;

      axios
        .get("/parcels/stats/parcel-revenue-per-location", { params })
        .then((response) => {
          const series = (response.data as typeof chartSeries) ?? [];
          const allTs = new Set<number>();
          series.forEach((s) => {
            const d = (s.data as [number, number][] | undefined) ?? [];
            d.forEach(([ts]) => allTs.add(ts));
          });
          const sortedTs = Array.from(allTs).sort((a, b) => a - b);

          const header =
            "Date," +
            series
              .map((s) => String(s?.name ?? "").replace(/,/g, " "))
              .join(",") +
            "\n";

          const rows = sortedTs.map((ts) => {
            const values = series.map((s) => {
              const pair = ((s.data as [number, number][]) ?? []).find(([t]) => t === ts);
              return pair != null ? String(pair[1] ?? "") : "";
            });

            return [dayjs(ts).format("YYYY-MM-DD"), ...values].join(",");
          });

          const csv = header + rows.join("\n");
          const blob = new Blob([csv], {
            type: "text/csv;charset=utf-8;",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `parcel_revenue_per_store_per_courier_daily_${dayjs().format("YYYY-MM-DD")}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error(
            "Error downloading CSV for ParcelRevenuePerStorePerCourier:",
            error
          );
        })
        .finally(() => {
          setIsCsvDownloading(false);
        });
    };

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div></div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
            {/* Courier selector - styled like MaterialVolumesPerMaterial and placed first */}
            <div className="flex items-center gap-2">
              <span className="font-satoshi text-sm font-medium text-gray-700 whitespace-nowrap">
                Courier:
              </span>
              <Menu>
                <MenuHandler>
                  <button className="h-9 px-3 sm:px-4 border-2 rounded-md border-gray-300 bg-gray-50 hover:bg-gray-100 flex items-center justify-center font-satoshi font-semibold text-gray-800 text-sm whitespace-nowrap min-w-[160px] shadow-sm">
                    {selectedCourierLabel}
                    <ChevronDown className="ml-1 sm:ml-2 w-4 h-4 flex-shrink-0" />
                  </button>
                </MenuHandler>
                <MenuList
                  className="rounded-none max-h-[300px] overflow-y-auto"
                  placeholder={undefined}
                >
                  <MenuItem
                    onClick={() => setSelectedCourierId("ALL")}
                    disabled={selectedCourierId === "ALL"}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    All couriers
                  </MenuItem>
                  {allCouriers?.map((courier) => (
                    <MenuItem
                      key={courier._id}
                      disabled={selectedCourierId === courier._id}
                      onClick={() =>
                        setSelectedCourierId(courier._id as string)
                      }
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {courier.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>

            <MaterialsChartFilters
              state={filters}
              onChange={setFilters}
              allowWeekly={false}
              hasData={!!(chartSeries && chartSeries.length > 0)}
              onDownloadImage={downloadScreenshot}
              onDownloadCsv={downloadCsv}
              isCsvDownloading={isCsvDownloading}
            />
          </div>
        </div>
        <div>
          <section ref={snapshotRef} className="mt-4">
            <div className="flex items-center mb-2 sm:mb-0">
              <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
                Parcel revenues per store per courier (€/
                {filters.period.charAt(0).toLowerCase() +
                  filters.period.slice(1).toLowerCase()}
                )
              </p>
              <Tooltip
                content="Non-cumulative: Shows parcel revenue for the selected date range"
                placeholder={undefined}
              >
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 ml-2 cursor-pointer"
                />
              </Tooltip>
            </div>
            {/* @ts-ignore */}
            {chartSeries && chartSeries.length > 0 ? (
              <Chart
                options={{
                  ...options,
                  colors:
                    parcelRevenueColors.length > 0
                      ? parcelRevenueColors
                      : options.colors,
                  xaxis: {
                    // Only show 1 tick when there's only one bucket
                    tickAmount: xTickAmount,
                    labels: {
                      // @ts-ignore
                      formatter: function (value: string | number) {
                        const ts = Number(value);
                        if (filters.period === "year") {
                          return dayjs(ts).format("YYYY");
                        }
                        if (filters.period === "quarter") {
                          const d = dayjs(ts);
                          const q = Math.ceil((d.month() + 1) / 3);
                          return `Q${q} ${d.year()}`;
                        }
                        return dayjs(ts).format("MMM YYYY");
                      },
                    },
                  },
                }}
                series={chartSeries}
                // type="line"
                height={300}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }
);

const options: ApexOptions = {
  chart: {
    type: "line",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  legend: {
    show: true,
    position: "right",
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      // Create an array of series with their values
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));

      // Sort by value in descending order (highest to lowest)
      seriesData.sort((a: any, b: any) => b.value - a.value);

      // Build the tooltip HTML with original ApexCharts styling
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM YYYY")}</div>`;

      seriesData.forEach((item: any) => {
        const formattedValue = new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(item.value);

        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${
              item.color
            };"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${
                  item.name
                }: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  yaxis: {
    title: {
      text: "Revenue (€)",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    labels: {
      formatter: function (value: number) {
        return new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      },
    },
  },
  colors: [
    "#e34e76",
    "#007887",
    "#8773aa",
    "#e6e1ff",
    "#fe3e3e",
    "#ffff78",
    "#d2e6ff",
    "#d2d2b2",
    "#5fd0ae",
    "#ff8dfa",
    "#ff6e41",
    "#808080",
  ],
};
